import { projectToNormalized, type CameraModel, type FaceFrame, type Vec3 } from '@ar-suite/core'
import { styleAsOverlay } from './scene'

/**
 * 2-D canvas drawn on top of the 3-D canvas for landmark / tesselation / anchor debugging.
 * Coordinates are normalized image coords, so it is mirrored the same way as the video.
 */
export class DebugOverlay2D {
  readonly canvas = document.createElement('canvas')
  private ctx: CanvasRenderingContext2D
  showLandmarks = true
  showTesselation = false
  private edges: { start: number; end: number }[] = []

  constructor(edges: { start: number; end: number }[] = []) {
    this.edges = edges
    const ctx = this.canvas.getContext('2d')
    if (!ctx) throw new Error('2D context unavailable')
    this.ctx = ctx
    this.canvas.style.pointerEvents = 'none'
  }

  mount(container: HTMLElement, mirror: boolean): void {
    styleAsOverlay(this.canvas, mirror)
    container.appendChild(this.canvas)
  }

  setMirror(mirror: boolean): void {
    this.canvas.style.transform = mirror ? 'scaleX(-1)' : 'none'
  }

  resize(width: number, height: number): void {
    this.canvas.width = width
    this.canvas.height = height
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  drawFace(frame: FaceFrame, highlight: number[] = []): void {
    const { ctx } = this
    const W = this.canvas.width, H = this.canvas.height
    const l = frame.landmarks
    const n = l.length / 3
    if (this.showTesselation) {
      ctx.strokeStyle = 'rgba(0,255,180,0.25)'
      ctx.lineWidth = 1
      ctx.beginPath()
      for (const e of this.edges) {
        if (e.start >= n || e.end >= n) continue
        ctx.moveTo(l[e.start * 3]! * W, l[e.start * 3 + 1]! * H)
        ctx.lineTo(l[e.end * 3]! * W, l[e.end * 3 + 1]! * H)
      }
      ctx.stroke()
    }
    if (this.showLandmarks) {
      ctx.fillStyle = 'rgba(0,255,120,0.8)'
      for (let i = 0; i < n; i++) {
        ctx.fillRect(l[i * 3]! * W - 1, l[i * 3 + 1]! * H - 1, 2, 2)
      }
    }
    ctx.fillStyle = '#ff3366'
    for (const i of highlight) {
      if (i >= n) continue
      ctx.beginPath()
      ctx.arc(l[i * 3]! * W, l[i * 3 + 1]! * H, 4, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  /** Draw a camera-space point (cm) as a cross. */
  drawPoint(p: Vec3, cam: CameraModel, color = '#ffcc00', size = 6): void {
    const uv = projectToNormalized([0, 0], cam, p)
    if (!uv) return
    const x = uv[0] * this.canvas.width, y = uv[1] * this.canvas.height
    const { ctx } = this
    ctx.strokeStyle = color
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(x - size, y)
    ctx.lineTo(x + size, y)
    ctx.moveTo(x, y - size)
    ctx.lineTo(x, y + size)
    ctx.stroke()
  }

  /** Draw the head axes (x red, y green, z blue) from the head matrix. */
  drawAxes(origin: Vec3, axes: [Vec3, Vec3, Vec3], cam: CameraModel): void {
    const o = projectToNormalized([0, 0], cam, origin)
    if (!o) return
    const colors = ['#ff4040', '#40ff40', '#4080ff']
    const { ctx } = this
    ctx.lineWidth = 2
    axes.forEach((a, i) => {
      const uv = projectToNormalized([0, 0], cam, a)
      if (!uv) return
      ctx.strokeStyle = colors[i]!
      ctx.beginPath()
      ctx.moveTo(o[0] * this.canvas.width, o[1] * this.canvas.height)
      ctx.lineTo(uv[0] * this.canvas.width, uv[1] * this.canvas.height)
      ctx.stroke()
    })
  }
}
